import { useCallback, useEffect, useState, useRef } from 'react';
import { ApiError, apiRequest } from '../../api/client.js';

export type PanelResultadoProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly reviewedRevision: number | null;
  onCambio(): void;
};

type Render = {
  readonly id: string;
  readonly revision: number;
  readonly status: string;
  readonly outputAssetId: string | null;
  readonly durationMs: number | null;
  readonly width: number | null;
  readonly height: number | null;
  readonly error: { kind?: string; message?: string } | null;
  readonly createdAt: string;
  readonly contentUrl: string | null;
  readonly stale: boolean;
};

const ESTADOS: Record<string, string> = {
  PENDING: 'En cola',
  RUNNING: 'Renderizando',
  COMPLETED: 'Completado',
  FAILED: 'Error',
  CANCELLED: 'Cancelado',
};

export function PanelResultado({
  projectId,
  revision,
  status,
  reviewedRevision,
  onCambio,
}: PanelResultadoProps): React.JSX.Element {
  const [renders, setRenders] = useState<Render[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const recargar = useCallback(async () => {
    try {
      setRenders(await apiRequest<Render[]>(`/projects/${projectId}/renders`));
      setCargado(true);
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudieron cargar los renders.');
    }
  }, [projectId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const enCurso = renders.some((r) => r.status === 'PENDING' || r.status === 'RUNNING');

  useEffect(() => {
    if (!enCurso) return;
    const intervalo = setInterval(() => void recargar(), 3_000);
    return () => clearInterval(intervalo);
  }, [enCurso, recargar]);

  // Igual que en la producción: cuando el render ya no está en curso pero la
  // página sigue en «Renderizando», se le pide que recargue el proyecto.
  const [cargado, setCargado] = useState(false);
  // Por referencia: la página pasa una función nueva en cada render y, como
  // dependencia, relanzaría el efecto sin que nada hubiera cambiado.
  const avisar = useRef(onCambio);
  avisar.current = onCambio;
  useEffect(() => {
    if (cargado && !enCurso && status === 'RENDERING') avisar.current();
  }, [cargado, enCurso, status]);

  async function accion(ruta: string, cuerpo?: Record<string, unknown>): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await apiRequest(ruta, { method: 'POST', ...(cuerpo === undefined ? {} : { body: cuerpo }) });
      await recargar();
      onCambio();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'La operación falló.');
    } finally {
      setOcupado(false);
    }
  }

  const aprobado = reviewedRevision === revision;
  const puedeRenderizar = aprobado && !enCurso && ['READY', 'COMPLETED'].includes(status);
  const actual = renders.find((r) => r.status === 'COMPLETED' && !r.stale);

  return (
    <section style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}>
      <h2 style={{ marginTop: 0 }}>Resultado</h2>

      {error !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}

      <p style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => void accion(`/projects/${projectId}/render`, { expectedRevision: revision })}
          disabled={ocupado || !puedeRenderizar}
          title={aprobado ? 'Generar el MP4' : 'Aprueba la revisión antes de renderizar'}
        >
          Renderizar MP4
        </button>
        {enCurso && (
          <button
            type="button"
            onClick={() => void accion(`/projects/${projectId}/render/cancel`)}
            disabled={ocupado}
          >
            Cancelar render
          </button>
        )}
      </p>

      {!aprobado && (
        <p>
          <small>Aprueba la revisión {revision} para poder renderizar.</small>
        </p>
      )}

      {actual !== undefined && actual.contentUrl !== null && (
        <>
          <h3>Vídeo actual</h3>
          <video
            src={actual.contentUrl}
            controls
            preload="metadata"
            style={{ width: '100%', maxWidth: '40rem', background: '#000' }}
          />
          <p>
            <a href={`${actual.contentUrl}?download=true`}>Descargar MP4</a> ·{' '}
            {actual.width}×{actual.height} ·{' '}
            {actual.durationMs === null ? '' : `${Math.round(actual.durationMs / 1000)} s`} · revisión{' '}
            {actual.revision}
          </p>
        </>
      )}

      {renders.length > 0 && (
        <>
          <h3>Historial</h3>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--borde)' }}>
                <th scope="col">Fecha</th>
                <th scope="col">Revisión</th>
                <th scope="col">Estado</th>
                <th scope="col">Vídeo</th>
              </tr>
            </thead>
            <tbody>
              {renders.map((render) => (
                <tr key={render.id} style={{ borderBottom: '1px solid var(--borde-suave)' }}>
                  <td>{new Date(render.createdAt).toLocaleString('es-ES')}</td>
                  <td>
                    {render.revision}
                    {/* Un render de una revisión anterior sigue descargable,
                        pero no es el resultado actual. */}
                    {render.stale && <em> (desactualizado)</em>}
                  </td>
                  <td>
                    {ESTADOS[render.status] ?? render.status}
                    {render.error !== null && (
                      <>
                        <br />
                        <small style={{ color: 'var(--error)' }}>{render.error.message}</small>
                      </>
                    )}
                  </td>
                  <td>
                    {render.contentUrl === null ? (
                      '—'
                    ) : (
                      <a href={`${render.contentUrl}?download=true`}>Descargar</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
